/**
 * Canonical marketing chrome source.
 *
 * Static-page generators and the Vue marketing shell both consume this
 * locale copy. Keep navigation labels, account labels, and footer links here
 * instead of copying them into individual page generators or components.
 */
export const MARKETING_COPY = {
  en: {
    homePath: '/',
    occasionsPath: '/occasions',
    howPath: '/#how-it-works',
    examplesPath: '/#examples',
    pricingPath: '/pricing',
    researchPath: '/research/group-card-index-2026',
    faqPath: '/faq',
    privacyPath: '/privacy',
    termsPath: '/terms',
    navLabel: 'Primary navigation',
    sectionsLabel: 'Homepage sections',
    homeLabel: 'Sharewell home',
    tagline: 'Free online group card maker',
    occasions: 'Occasions',
    how: 'How it works',
    examples: 'Examples',
    pricing: 'Pricing',
    faq: 'FAQ',
    signIn: 'Sign in',
    signUp: 'Sign up',
    account: 'Account',
    myCards: 'My cards',
    accountSettings: 'Account settings',
    admin: 'Admin',
    logOut: 'Log out',
    footerCopy: "Made with love for life's most meaningful moments.",
    footerNavigationLabel: 'Footer navigation',
    productGroup: 'Product',
    resourcesGroup: 'Resources',
    supportGroup: 'Help & legal',
    blog: 'Blog',
    research: 'Research',
    help: 'Help center',
    privacy: 'Privacy',
    terms: 'Terms',
    socialLabel: 'Sharewell social links',
    instagramLabel: 'Sharewell on Instagram',
    languageLabel: 'Choose language',
    menuOpen: 'Open menu',
    menuClose: 'Close menu',
    skip: 'Skip to content',
  },
  fr: {
    homePath: '/fr',
    occasionsPath: '/fr/occasions',
    howPath: '/fr#how-it-works',
    examplesPath: '/fr#examples',
    pricingPath: '/fr/pricing',
    researchPath: '/fr/research/group-card-index-2026',
    faqPath: '/fr/faq',
    privacyPath: '/fr/privacy',
    termsPath: '/fr/terms',
    navLabel: 'Navigation principale',
    sectionsLabel: "Sections de la page d'accueil",
    homeLabel: 'Accueil Sharewell',
    tagline: 'Cartes de groupe gratuites en ligne',
    occasions: 'Occasions',
    how: 'Fonctionnement',
    examples: 'Exemples',
    pricing: 'Tarifs',
    faq: 'FAQ',
    signIn: 'Se connecter',
    signUp: 'S’inscrire',
    account: 'Compte',
    myCards: 'Mes cartes',
    accountSettings: 'Paramètres du compte',
    admin: 'Administration',
    logOut: 'Déconnexion',
    footerCopy: 'Créé avec amour pour les moments les plus importants de la vie.',
    footerNavigationLabel: 'Navigation de pied de page',
    productGroup: 'Produit',
    resourcesGroup: 'Ressources',
    supportGroup: 'Aide et mentions légales',
    blog: 'Blog',
    research: 'Études',
    help: 'Aide',
    privacy: 'Confidentialité',
    terms: 'Conditions',
    socialLabel: 'Liens sociaux Sharewell',
    instagramLabel: 'Sharewell sur Instagram',
    languageLabel: 'Choisir la langue',
    menuOpen: 'Ouvrir le menu',
    menuClose: 'Fermer le menu',
    skip: 'Aller au contenu',
  },
  es: {
    homePath: '/es',
    occasionsPath: '/es/occasions',
    howPath: '/es#how-it-works',
    examplesPath: '/es#examples',
    pricingPath: '/es/pricing',
    researchPath: '/research/group-card-index-2026',
    faqPath: '/es/faq',
    privacyPath: '/es/privacy',
    termsPath: '/es/terms',
    navLabel: 'Navegación principal',
    sectionsLabel: 'Secciones de la página de inicio',
    homeLabel: 'Inicio de Sharewell',
    tagline: 'Creador gratuito de tarjetas de grupo online',
    occasions: 'Ocasiones',
    how: 'Cómo funciona',
    examples: 'Ejemplos',
    pricing: 'Precios',
    faq: 'FAQ',
    signIn: 'Iniciar sesión',
    signUp: 'Registrarse',
    account: 'Cuenta',
    myCards: 'Mis tarjetas',
    accountSettings: 'Configuración de la cuenta',
    admin: 'Administración',
    logOut: 'Cerrar sesión',
    footerCopy: 'Hecho con amor para los momentos más importantes de la vida.',
    footerNavigationLabel: 'Navegación del pie de página',
    productGroup: 'Producto',
    resourcesGroup: 'Recursos',
    supportGroup: 'Ayuda y legal',
    blog: 'Blog',
    research: 'Investigación',
    help: 'Ayuda',
    privacy: 'Privacidad',
    terms: 'Condiciones',
    socialLabel: 'Enlaces sociales de Sharewell',
    instagramLabel: 'Sharewell en Instagram',
    languageLabel: 'Elegir idioma',
    menuOpen: 'Abrir el menú',
    menuClose: 'Cerrar el menú',
    skip: 'Saltar al contenido',
  },
};

function copyFor(locale) {
  const copy = MARKETING_COPY[locale];
  if (!copy) throw new Error(`Unsupported marketing locale: ${locale}`);
  return copy;
}

export function renderMarketingNav(locale) {
  const copy = copyFor(locale);
  return `<nav class="mock-nav home-shell" aria-label="${copy.navLabel}">
      <div class="mock-brand">
        <a href="${copy.homePath}" class="mock-logo" aria-label="${copy.homeLabel}">
          <img src="/images/logo/logo.svg" alt="Sharewell" width="154" height="57">
        </a>
        <p class="mock-tagline">${copy.tagline}</p>
      </div>
      <div class="mock-nav-links" aria-label="${copy.sectionsLabel}">
        <a href="${copy.occasionsPath}">${copy.occasions}</a>
        <a href="${copy.howPath}">${copy.how}</a>
        <a href="${copy.examplesPath}">${copy.examples}</a>
        <a href="${copy.pricingPath}">${copy.pricing}</a>
        <a href="${copy.faqPath}">${copy.faq}</a>
      </div>
      <div class="mock-account-area">
        <div class="mock-auth" id="authLinks">
          <a href="/signin">${copy.signIn}</a>
          <a href="/signup" class="mock-signup">${copy.signUp}</a>
        </div>
        <div class="user-dropdown" id="userDropdown" style="display: none;">
          <button class="dropdown-trigger" type="button" id="dropdownTrigger" aria-expanded="false" aria-controls="dropdownMenu">
            <span id="userName">${copy.account}</span>
            <span class="dropdown-arrow" aria-hidden="true"></span>
          </button>
          <div class="dropdown-menu" id="dropdownMenu">
            <a href="/my-cards" class="dropdown-item">${copy.myCards}</a>
            <a href="/account" class="dropdown-item">${copy.accountSettings}</a>
            <a href="/admin" class="dropdown-item" id="adminMenuItem" style="display: none;">${copy.admin}</a>
            <a href="/logout" class="dropdown-item">${copy.logOut}</a>
          </div>
        </div>
        ${renderMarketingMobileMenuToggle(copy)}
      </div>
    </nav>
    ${renderMarketingMobileMenu(copy)}`;
}

function renderMarketingMobileMenuToggle(copy) {
  return `<button class="mock-mobile-menu-toggle" type="button" id="mobileMenuToggle" aria-expanded="false" aria-controls="mobileMenu" aria-haspopup="dialog" aria-label="${copy.menuOpen}">
          <span aria-hidden="true">…</span>
        </button>`;
}

function renderMarketingMobileMenu(copy) {
  return `<div class="mock-mobile-menu" id="mobileMenu" data-open="false" aria-hidden="true" role="dialog" aria-modal="true">
          <div class="mock-mobile-menu-head">
            <a href="${copy.homePath}" class="mock-mobile-menu-logo" aria-label="${copy.homeLabel}">
              <img src="/images/logo/logo.svg" alt="Sharewell" width="154" height="57">
            </a>
            <button class="mock-mobile-menu-close" type="button" id="mobileMenuClose" aria-label="${copy.menuClose}">
              <span aria-hidden="true">×</span>
            </button>
          </div>
          <nav class="mock-mobile-menu-nav" aria-label="${copy.navLabel}">
            <a href="${copy.occasionsPath}">${copy.occasions}</a>
            <a href="${copy.howPath}">${copy.how}</a>
            <a href="${copy.examplesPath}">${copy.examples}</a>
            <a href="${copy.pricingPath}">${copy.pricing}</a>
            <a href="${copy.faqPath}">${copy.faq}</a>
            <div class="mock-mobile-menu-divider" aria-hidden="true"></div>
            <div class="mock-mobile-auth" id="mobileAuthLinks">
              <a href="/signin">${copy.signIn}</a>
              <a href="/signup">${copy.signUp}</a>
            </div>
            <div class="mock-mobile-user" id="mobileUserLinks" style="display: none;">
              <a href="/my-cards">${copy.myCards}</a>
              <a href="/account">${copy.accountSettings}</a>
              <a href="/admin" id="mobileAdminMenuItem" style="display: none;">${copy.admin}</a>
              <a href="/logout">${copy.logOut}</a>
            </div>
          </nav>
    </div>`;
}

export function renderMarketingHeader(locale) {
  const copy = copyFor(locale);
  return `<a class="skip-link" href="#main">${copy.skip}</a>
  <header class="mock-header" id="top">
    ${renderMarketingNav(locale)}
  </header>`;
}

export function renderMarketingAccountScript(locale) {
  const copy = copyFor(locale);
  return `<script data-sharewell-marketing-account>
    (() => {
      const authLinks = document.getElementById('authLinks');
      const userDropdown = document.getElementById('userDropdown');
      const dropdownTrigger = document.getElementById('dropdownTrigger');
      const dropdownMenu = document.getElementById('dropdownMenu');
      const dropdownArrow = document.querySelector('.dropdown-arrow');
      const adminMenuItem = document.getElementById('adminMenuItem');
      const userName = document.getElementById('userName');
      if (!authLinks || !userDropdown || !dropdownTrigger || !dropdownMenu || !adminMenuItem || !userName) return;

      fetch('/auth/me')
        .then(response => {
          if (!response.ok) throw new Error('Not authenticated');
          return response.json();
        })
        .then(payload => {
          const user = payload && payload.user ? payload.user : payload;
          userName.textContent = user && user.email ? user.email : '${copy.account}';
          adminMenuItem.style.display = user && user.is_admin ? 'block' : 'none';
          authLinks.style.display = 'none';
          userDropdown.style.display = 'block';
        })
        .catch(() => {
          authLinks.style.display = 'flex';
          userDropdown.style.display = 'none';
        });

      dropdownTrigger.addEventListener('click', event => {
        event.stopPropagation();
        const isOpen = dropdownMenu.classList.toggle('show');
        dropdownTrigger.setAttribute('aria-expanded', String(isOpen));
        dropdownArrow?.classList.toggle('arrow-up', isOpen);
      });

      document.addEventListener('click', () => {
        dropdownMenu.classList.remove('show');
        dropdownTrigger.setAttribute('aria-expanded', 'false');
        dropdownArrow?.classList.remove('arrow-up');
      });
    })();
  </script>
  ${renderMarketingMobileMenuScript(locale)}`;
}

export function renderMarketingMobileMenuScript(locale) {
  copyFor(locale);
  return `<script data-sharewell-marketing-mobile>
    (() => {
      const mobileMenuToggle = document.getElementById('mobileMenuToggle');
      const mobileMenu = document.getElementById('mobileMenu');
      const mobileMenuClose = document.getElementById('mobileMenuClose');
      const mobileAuthLinks = document.getElementById('mobileAuthLinks');
      const mobileUserLinks = document.getElementById('mobileUserLinks');
      const mobileAdminMenuItem = document.getElementById('mobileAdminMenuItem');
      if (!mobileMenuToggle || !mobileMenu || !mobileMenuClose) return;

      const syncMobileAccount = user => {
        const isAuthenticated = Boolean(user);
        if (mobileAuthLinks) mobileAuthLinks.style.display = isAuthenticated ? 'none' : 'grid';
        if (mobileUserLinks) mobileUserLinks.style.display = isAuthenticated ? 'grid' : 'none';
        if (mobileAdminMenuItem) mobileAdminMenuItem.style.display = isAuthenticated && user.is_admin ? 'block' : 'none';
      };

      const setMobileMenuOpen = (isOpen, restoreFocus = false) => {
        mobileMenu.dataset.open = String(isOpen);
        mobileMenu.setAttribute('aria-hidden', String(!isOpen));
        mobileMenuToggle.setAttribute('aria-expanded', String(isOpen));
        document.documentElement.classList.toggle('mobile-menu-open', isOpen);
        if (isOpen) mobileMenuClose.focus();
        if (!isOpen && restoreFocus) mobileMenuToggle.focus();
      };

      mobileMenuToggle.addEventListener('click', event => {
        event.stopPropagation();
        setMobileMenuOpen(mobileMenu.dataset.open !== 'true');
      });
      mobileMenuClose.addEventListener('click', () => setMobileMenuOpen(false, true));
      mobileMenu.querySelectorAll('a').forEach(link => {
        link.addEventListener('click', () => setMobileMenuOpen(false));
      });
      document.addEventListener('keydown', event => {
        if (event.key === 'Escape' && mobileMenu.dataset.open === 'true') {
          setMobileMenuOpen(false, true);
        }
      });

      fetch('/auth/me')
        .then(response => {
          if (!response.ok) throw new Error('Not authenticated');
          return response.json();
        })
        .then(payload => {
          const user = payload && payload.user ? payload.user : payload;
          syncMobileAccount(user);
        })
        .catch(() => syncMobileAccount(null));
    })();
  </script>`;
}

export function renderMarketingFooterMarkup(locale) {
  const copy = copyFor(locale);
  return `<footer class="mock-footer">
    <div class="mock-footer-inner home-shell">
      <div class="mock-footer-brand">
        <a href="${copy.homePath}" aria-label="${copy.homeLabel}"><img src="/images/logo/logo.svg" alt="Sharewell" width="148" height="55"></a>
        <p>${copy.footerCopy}</p>
      </div>
      <nav class="mock-footer-links" aria-label="${copy.footerNavigationLabel}">
        <section class="mock-footer-group">
          <h2>${copy.productGroup}</h2>
          <div>
            <a href="${copy.occasionsPath}">${copy.occasions}</a>
            <a href="${copy.howPath}">${copy.how}</a>
            <a href="${copy.examplesPath}">${copy.examples}</a>
            <a href="${copy.pricingPath}">${copy.pricing}</a>
          </div>
        </section>
        <section class="mock-footer-group">
          <h2>${copy.resourcesGroup}</h2>
          <div>
            ${locale === 'fr' ? `<a href="/blog/">${copy.blog}</a>
            ` : ''}<a href="${copy.researchPath}">${copy.research}</a>
          </div>
        </section>
        <section class="mock-footer-group">
          <h2>${copy.supportGroup}</h2>
          <div>
            <a href="${copy.faqPath}">${copy.help}</a>
            <a href="${copy.privacyPath}">${copy.privacy}</a>
            <a href="${copy.termsPath}">${copy.terms}</a>
          </div>
        </section>
      </nav>
      <div class="mock-social" aria-label="${copy.socialLabel}">
        <a href="https://www.instagram.com/sharewell.io/" aria-label="${copy.instagramLabel}">
          <svg viewBox="0 0 24 24" focusable="false" aria-hidden="true"><rect x="5" y="5" width="14" height="14" rx="4"></rect><circle cx="12" cy="12" r="3"></circle><path d="M16.5 7.5h.01"></path></svg>
        </a>
        <select id="languageSelect" class="language-select" aria-label="${copy.languageLabel}">
          <option value="en"${locale === 'en' ? ' selected' : ''}>English</option>
          <option value="fr"${locale === 'fr' ? ' selected' : ''}>Français</option>
          <option value="es"${locale === 'es' ? ' selected' : ''}>Español</option>
        </select>
      </div>
    </div>
  </footer>`;
}

export function renderMarketingFooter(locale, { languageRoutes = {} } = {}) {
  const copy = copyFor(locale);
  const routes = {
    en: languageRoutes.en || '/',
    fr: languageRoutes.fr || '/fr',
    es: languageRoutes.es || '/es',
  };

  return `${renderMarketingFooterMarkup(locale)}
  ${renderMarketingMobileMenuScript(locale)}
  <script>
    (() => {
      const authLinks = document.getElementById('authLinks');
      const userDropdown = document.getElementById('userDropdown');
      const dropdownTrigger = document.getElementById('dropdownTrigger');
      const dropdownMenu = document.getElementById('dropdownMenu');
      const dropdownArrow = document.querySelector('.dropdown-arrow');
      const adminMenuItem = document.getElementById('adminMenuItem');
      const userName = document.getElementById('userName');

      fetch('/auth/me')
        .then(response => {
          if (!response.ok) throw new Error('Not authenticated');
          return response.json();
        })
        .then(payload => {
          const user = payload && payload.user ? payload.user : payload;
          userName.textContent = user && user.email ? user.email : '${copy.account}';
          adminMenuItem.style.display = user && user.is_admin ? 'block' : 'none';
          authLinks.style.display = 'none';
          userDropdown.style.display = 'block';
        })
        .catch(() => {
          authLinks.style.display = 'flex';
          userDropdown.style.display = 'none';
        });

      dropdownTrigger.addEventListener('click', event => {
        event.stopPropagation();
        const isOpen = dropdownMenu.classList.toggle('show');
        dropdownTrigger.setAttribute('aria-expanded', String(isOpen));
        dropdownArrow.classList.toggle('arrow-up', isOpen);
      });

      document.addEventListener('click', () => {
        dropdownMenu.classList.remove('show');
        dropdownTrigger.setAttribute('aria-expanded', 'false');
        dropdownArrow.classList.remove('arrow-up');
      });

      const languageSelect = document.getElementById('languageSelect');
      const languageRoutes = ${JSON.stringify(routes)};
      languageSelect?.addEventListener('change', function () {
        const selectedLanguage = this.value;
        const expirationDate = new Date();
        expirationDate.setFullYear(expirationDate.getFullYear() + 1);
        document.cookie = 'lang=' + selectedLanguage + ';expires=' + expirationDate.toUTCString() + ';path=/;samesite=lax';
        try {
          localStorage.setItem('lang', selectedLanguage);
        } catch (error) {
          // The language cookie still carries the preference.
        }
        window.location.href = languageRoutes[selectedLanguage] || '/';
      });
    })();
  </script>`;
}
